import React, {PureComponent} from 'react';
import _ from 'lodash';
import Modal from './Modal';
import {log} from '@elixir/logger';

/**
 * The variable used to remember the "singleton" instance of the `ModalProvider` component
 */
let providerSingleton = null;

/**
 * The `ModalProvider` component is designed to be a "singleton" component used once on an application "page" to
 * provide a rendering space for the modal dialog that can be show/dismissed by the `ModalService`.  It provides a
 * set of static methods, `showDialogWithProps()`, `closeModal()` and `dismissModal()`, that are used by `ModalService`.
 * It is possible for other callers to also use this static API, but it is discouraged in favor of the more prescribed
 * methods of the `ModalService`.
 */
export default class ModalProvider extends PureComponent {
    /**
     * The initial state of the `ModalProvider`.  Both the `modalDialogProps` and `showModal` data values are
     * initialized to `null` to indicate that no `Modal` is currently active.
     *
     * @type {{modalDialogProps: null, showModal: null}}
     */
    state = {
        modalDialogProps: null,
        showModal: null
    };

    /**
     * Calls the `showDialog()` method of the "singleton" instance of the `ModalProvider` with the given `dialogProps`
     *
     * @param {Object} dialogProps - The props to use for the `Modal` dialog during the render
     */
    static showDialogWithProps (dialogProps) {
        if (providerSingleton) {
            providerSingleton.showDialog(dialogProps);
        }
        else {
            log.error(`ModalProvider has NOT been initialized, ignoring dialog request with props, "${dialogProps}"`);
        }
    }

    /**
     * If there is an open `Modal` dialog, will call it's `closeModal()` method with the given result
     *
     * @param {*} result - The result of the close modal request
     */
    static closeModal (result) {
        if (providerSingleton && providerSingleton._modal) {
            providerSingleton._modal.closeModal(result);
        }
        else {
            log.error('ModalProvider has either NOT been initialized or the modal is not shown, ignoring close request');
        }
    }

    /**
     * If there is an open `Modal` dialog, will call it's `dismissModal()` method with the given result
     *
     * @param {*} result - The result of the dismiss modal request
     */
    static dismissModal (result) {
        if (providerSingleton && providerSingleton._modal) {
            providerSingleton._modal.dismissModal(result);
        }
        else {
            log.error('ModalProvider has either NOT been initialized or the modal is not shown, ignoring dismiss request');
        }
    }

    /**
     * React life-cycle method called when the component has mounted.  Set the "singleton" to this instance.
     *
     * @throws {Error} - When this is not the first instance of a `ModalProvider` being mounted
     */
    componentDidMount () {
        if (providerSingleton) {
            throw new Error('Attempting to create an additional ModalProvider, only one is allowed');
        }
        providerSingleton = this;
    }

    /**
     * React life-cycle method called when the component is being unmounted.  Will reset the "singleton" instance back
     * to `null` so that another instance can be used.
     */
    componentWillUnmount () { // eslint-disable-line class-methods-use-this
        providerSingleton = null;
    }

    /**
     * Will enhance the given `dialogProps` with the `onClosed` and `toggle` callbacks, removing the `isOpen` prop and
     * then set the `modalDialogProps` state to the updated props.  Also sets the `showModal` state to true thereby
     * causing the `Modal` dialog to be shown.
     *
     * @param {Object} dialogProps - The props to use for the `Modal` dialog during the render
     * @throws {TypeError} - When the `dialogProps` is not an Object
     */
    showDialog (dialogProps) {
        if (!_.isObject(dialogProps)) {
            throw new TypeError('Attempting to show a dialog without providing an "props" object');
        }
        const modalDialogProps = Object.assign({}, dialogProps, {
            onClosed: () => {
                this.dialogRemoved();
                if (_.isFunction(dialogProps.onClosed)) {
                    dialogProps.onClosed();
                }
            },
            toggle: this.hideDialog
        });

        delete modalDialogProps.isOpen;

        this.setState({modalDialogProps, showModal: true});
    }

    /**
     * Hides the `Modal` dialog by setting the `showModal` state to false
     */
    hideDialog = () => {
        this.setState({showModal: false});
    };

    /**
     * After the dialog is removed, we reset the `modalDialogProps` and `showModal` props back to `null` and clearing
     * the `_modal` reference.
     */
    dialogRemoved = () => {
        this._modal = null;
        this.setState({modalDialogProps: null, showModal: null});
    };

    /**
     * Renders the `Modal` dialog if there are `modalDialogProps` provided for it
     *
     * @returns {ReactElement|null} - The rendered `Modal` dialog if there are props for it, otherwise null
     */
    render () {
        const {modalDialogProps, showModal} = this.state;
        if (!modalDialogProps) {
            return null;
        }
        return <Modal isOpen={showModal} ref={(c) => { this._modal = c; }} {...modalDialogProps}/>;
    }
}